import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A thumbnail with a caption under it: the unit of any image grid.
 *
 * The frame is a fixed square regardless of the image's own shape, so a grid of mixed portrait
 * and landscape uploads still reads as a grid. The caption carries a name and one line of facts
 * about the image — on the left what it is (size, dimensions), on the right what it is doing
 * (used, unused, selected).
 *
 * `onImageLoad` reports the image's natural dimensions, which is how a caller shows them without
 * the server having to store them.
 */
export default function ImageCard({
  src,
  alt,
  loading,
  title,
  meta,
  aside,
  selected,
  disabled,
  onClick,
  onImageLoad,
  className,
}) {
  const [failed, setFailed] = useState(false);
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';

  return (
    <Tag
      type={interactive ? 'button' : undefined}
      className={classNames(
        'mntl-image-card',
        interactive && 'mntl-image-card--interactive',
        selected && 'mntl-image-card--selected',
        className,
      )}
      onClick={onClick}
      disabled={interactive ? disabled : undefined}
      title={title}
      aria-pressed={interactive && selected ? true : undefined}
    >
      <span className="mntl-image-card__frame">
        {loading && <span className="spinner-border spinner-border-sm text-muted" role="status" />}
        {!loading && src && !failed && (
          <img
            src={src}
            alt={alt || title || ''}
            onLoad={(e) => onImageLoad?.({
              width: e.currentTarget.naturalWidth,
              height: e.currentTarget.naturalHeight,
            })}
            onError={() => setFailed(true)}
          />
        )}
        {!loading && (!src || failed) && (
          <i className="bi bi-image text-muted fs-3" aria-hidden />
        )}
      </span>

      <span className="mntl-image-card__body">
        <span className="mntl-image-card__title">{title}</span>
        {(meta || aside) && (
          <span className="mntl-image-card__meta">
            <span className="text-muted text-truncate">{meta}</span>
            {aside}
          </span>
        )}
      </span>
    </Tag>
  );
}

ImageCard.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  meta: PropTypes.node,
  aside: PropTypes.node,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onImageLoad: PropTypes.func,
  className: PropTypes.string,
};

ImageCard.defaultProps = {
  src: null,
  alt: undefined,
  loading: false,
  meta: null,
  aside: null,
  selected: false,
  disabled: false,
  onClick: undefined,
  onImageLoad: undefined,
  className: '',
};
